import { get } from '@src/packages/utils/storage';
import type { MarkingPortalSession } from '@src/models/interfaces/markingPortal.interface';

const MARKING_PORTAL_SESSION_STORAGE_KEY = 'markingPortal.activeSession';

export interface PersistedMarkingPortalSession {
  portalToken: string;
  authUserId: string;
  email: string;
  session: MarkingPortalSession;
  expiresAt: number;
  organizationId?: string;
  attendanceRecordId?: string;
}

export const getMarkingPortalAuthUserId = (): string | null => get('user_id') ?? null;

export const computeSessionSecondsRemaining = (expiresAt: number): number =>
  Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));

export const createSessionExpiresAt = (durationSeconds: number): number =>
  Date.now() + durationSeconds * 1000;

const tokensMatch = (storedToken: string, currentToken: string | null): boolean =>
  storedToken === (currentToken ?? '');

export const persistMarkingPortalSession = (data: PersistedMarkingPortalSession): void => {
  sessionStorage.setItem(MARKING_PORTAL_SESSION_STORAGE_KEY, JSON.stringify(data));
};

export const readMarkingPortalSession = (
  portalToken: string | null,
  authUserId: string | null = getMarkingPortalAuthUserId(),
): PersistedMarkingPortalSession | null => {
  const raw = sessionStorage.getItem(MARKING_PORTAL_SESSION_STORAGE_KEY);
  if (!raw || !authUserId) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as PersistedMarkingPortalSession;

    if (!tokensMatch(parsed.portalToken, portalToken)) {
      return null;
    }

    if (parsed.authUserId !== authUserId) {
      return null;
    }

    if (computeSessionSecondsRemaining(parsed.expiresAt) <= 0) {
      clearMarkingPortalSession();
      return null;
    }

    return parsed;
  } catch {
    clearMarkingPortalSession();
    return null;
  }
};

export const clearMarkingPortalSession = (): void => {
  sessionStorage.removeItem(MARKING_PORTAL_SESSION_STORAGE_KEY);
};
